import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Body3Bold, Body3Regular, CaptionBold, Subtitle1Bold } from '../../../lib/font';
import { colors } from '../../../lib/colors';
import { Col, Row, getItemWithExpiration } from '../../../lib/utils';
import { I_POPUP_EXIT } from '../../../types/images';
import { getCoachNameListAPI } from '../../../api/center';
import {
    deleteNewOpportunityAPI,
    getNewOpportunityCategoriesAPI,
    getNewOpportunityJournalAPI,
    NewOpportunityDTO,
    updateNewOpportunityAPI,
} from '../../../api/newOpportunity';
import { formatNewOpportunityPhoneNumber } from '../../../utils/newOpportunityPhone';
import { resolveScheduleAppLink } from '../../../lib/scheduleMemberLink';
import NewOpportunityJournalReadOnly from '../../NewOpportunity/NewOpportunityJournalReadOnly';
import { isMemberRecordReadOnly } from '../../../features/publicMember/types';

const ModalOverLay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #00000047;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
`;

const ModalInner = styled.div`
    width: 860px;
    max-height: calc(100vh - 80px);
    border-radius: 14px;
    background-color: #fdfeff;
    box-shadow: 8px 8px 32px 0px #0000007a;
    display: flex;
    flex-direction: column;
    padding: 32px 40px 36px;
    overflow-y: auto;

    @media (max-width: 900px) {
        width: calc(100vw - 40px);
        max-height: calc(100vh - 40px);
        padding: 24px;
    }

    @media (max-width: 600px) {
        width: calc(100vw - 20px);
        max-height: calc(100vh - 20px);
        padding: 18px 14px 22px;
        border-radius: 10px;

        .new-opportunity-detail-form,
        .new-opportunity-detail-time-range {
            grid-template-columns: minmax(0, 1fr) !important;
        }

        .new-opportunity-detail-time-selects,
        .new-opportunity-detail-actions {
            flex-wrap: wrap;
        }
    }
`;

const CloseButton = styled.button`
    width: 36px;
    height: 36px;
    flex: 0 0 auto;
    border: 1px solid #e2e8f0;
    border-radius: 9px;
    background: #ffffff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: #f8fafc;
    }

    &:focus-visible {
        outline: 2px solid #3d73dd;
        outline-offset: 2px;
    }
`;

type CategoryOption = {
    _id?: string;
    id?: string;
    name: string;
    isActive?: boolean;
};

type CoachOption = {
    coachId: string;
    name: string;
};

type FormState = {
    coachId: string;
    categoryId: string;
    isNoShow: boolean;
    scheduleDate: string;
    startClock: string;
    endClock: string;
    memo: string;
};
type FormField = keyof FormState;

type Props = {
    modalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    detailObject: any;
    setDetailObject: any;
    createAlert?: any;
};

const getId = (item?: { _id?: string; id?: string }) => item?._id || item?.id || '';
const toDateLocal = (value: string) => (value ? dayjs(value).format('YYYY-MM-DD') : '');
const toTimeLocal = (value: string) => (value ? dayjs(value).format('HH:mm') : '');
const combineDateAndTime = (date: string, time: string) => dayjs(`${date}T${time}`);

export const makeNewOpportunityFormState = (
    opportunity?: Partial<NewOpportunityDTO> | null,
    fallback?: Partial<FormState>
): FormState => ({
    coachId: opportunity?.coachId ?? fallback?.coachId ?? '',
    categoryId: opportunity?.categoryId ?? fallback?.categoryId ?? '',
    isNoShow:
        opportunity?.isNoShow === true ||
        opportunity?.executionStatus === 'NO_SHOW' ||
        fallback?.isNoShow === true,
    scheduleDate:
        toDateLocal(opportunity?.startTime || '') || fallback?.scheduleDate || '',
    startClock: toTimeLocal(opportunity?.startTime || '') || fallback?.startClock || '',
    endClock: toTimeLocal(opportunity?.endTime || '') || fallback?.endClock || '',
    memo: opportunity?.memo ?? fallback?.memo ?? '',
});

export const buildNewOpportunityUpdatePatch = (
    form: FormState,
    dirtyFields: Iterable<FormField | string>,
    expectedUpdatedAt?: string
) => {
    const dirty = new Set(dirtyFields);
    const patch: Record<string, string | boolean> = {};
    if (dirty.has('coachId')) patch.coachId = form.coachId;
    if (dirty.has('categoryId')) patch.categoryId = form.categoryId;
    if (dirty.has('isNoShow')) patch.isNoShow = form.isNoShow;
    if (dirty.has('memo')) patch.memo = form.memo;
    if (
        dirty.has('scheduleDate') ||
        dirty.has('startClock') ||
        dirty.has('endClock')
    ) {
        patch.startTime = combineDateAndTime(
            form.scheduleDate,
            form.startClock
        ).toISOString();
        patch.endTime = combineDateAndTime(
            form.scheduleDate,
            form.endClock
        ).toISOString();
    }
    // 구버전 데이터처럼 updatedAt이 없는 경우에는 필드를 생략해 하위 호환한다.
    if (expectedUpdatedAt) patch.expectedUpdatedAt = expectedUpdatedAt;
    return patch;
};

export const mergeLatestNewOpportunityForm = (
    current: FormState,
    latestOpportunity: Partial<NewOpportunityDTO>,
    dirtyFields: Iterable<FormField | string>
) => {
    const dirty = new Set(dirtyFields);
    const latest = makeNewOpportunityFormState(latestOpportunity);
    const next = { ...current };
    (Object.keys(latest) as FormField[]).forEach((field) => {
        if (!dirty.has(field)) next[field] = latest[field] as never;
    });
    return next;
};

export const getNewOpportunityUpdateConflictMessage = (
    error: any,
    latestRefreshed = false
) => {
    const serverMessage = String(error?.response?.data?.message || '');
    if (/participant|assigned|ownership|member|coach|담당|회원/i.test(serverMessage)) {
        return `운동일지가 연결된 신규기회는 회원이나 담당 강사를 변경할 수 없습니다. 강사 화면에서 운동일지를 먼저 삭제해주세요. 입력한 변경 내용은 유지했습니다.${
            latestRefreshed ? ' 최신 서버 정보도 다시 불러왔습니다.' : ''
        }`;
    }
    return latestRefreshed
        ? '다른 화면에서 신규기회 정보가 변경되었습니다. 입력한 변경 내용은 유지했고 최신 서버 정보를 다시 불러왔습니다. 내용을 확인한 뒤 다시 저장해주세요.'
        : '다른 화면에서 신규기회 정보가 변경되었습니다. 입력한 변경 내용은 유지했지만 최신 서버 정보를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.';
};

export const recoverNewOpportunityUpdateConflict = async (
    error: any,
    refetchLatest: () => Promise<any>
) => {
    let latestRefreshed = false;
    try {
        const result = await refetchLatest();
        latestRefreshed = result?.isError !== true;
    } catch {
        latestRefreshed = false;
    }
    return {
        latestRefreshed,
        message: getNewOpportunityUpdateConflictMessage(
            error,
            latestRefreshed
        ),
    };
};

export const isNewOpportunityReadOnly = (opportunity?: Partial<NewOpportunityDTO> | null) =>
    isMemberRecordReadOnly(opportunity);

// 오전/오후 + 시 + 분 3개 dropdown 헬퍼
const HOUR_OPTIONS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTE_OPTIONS_10 = [0, 10, 20, 30, 40, 50];

// "HH:mm" → { period, hour12, minute }
const splitClock = (
    value: string
): { period: 'AM' | 'PM'; hour12: number; minute: number } => {
    if (!value) return { period: 'AM', hour12: 9, minute: 0 };
    const [hStr, mStr] = value.split(':');
    const h24 = Number(hStr);
    const mRaw = Number(mStr);
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return {
        period: h24 < 12 ? 'AM' : 'PM',
        hour12: Number.isFinite(h12) ? h12 : 9,
        minute: Number.isFinite(mRaw) ? Math.floor(mRaw / 10) * 10 : 0,
    };
};

const joinClock = (parts: { period: 'AM' | 'PM'; hour12: number; minute: number }): string => {
    let h24 = parts.hour12 % 12;
    if (parts.period === 'PM') h24 += 12;
    return `${String(h24).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
};

const Field = ({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) => (
    <Col style={{ gap: 10 }}>
        <Body3Bold style={{ color: colors.LAVEL_4, fontSize: 16 }}>{label}</Body3Bold>
        {children}
    </Col>
);

const NewOpportunityDetailModal = ({
    modalVisible,
    setModalVisible,
    detailObject,
    setDetailObject,
    createAlert,
}: Props) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [authorization] = useState(getItemWithExpiration('authorization'));
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId = authObject?.centerId || detailObject?.centerId;
    const newOpportunityId = detailObject?.newOpportunityId || detailObject?._id || detailObject?.id;
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const [form, setForm] = useState<FormState>(() =>
        makeNewOpportunityFormState(detailObject)
    );
    const [dirtyFields, setDirtyFields] = useState<Set<FormField>>(
        () => new Set()
    );
    const [conflictNotice, setConflictNotice] = useState('');

    const notify = (title: string, message: string, isError = false) => {
        if (createAlert) createAlert(title, message, isError);
        else alert(message);
    };

    const close = () => {
        setModalVisible(false);
        setDetailObject(null);
    };
    const closeRef = useRef(close);
    closeRef.current = close;

    useEffect(() => {
        if (!modalVisible) return undefined;
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        closeButtonRef.current?.focus();
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeRef.current();
                return;
            }
            if (event.key !== 'Tab') return;
            const focusable = Array.from(
                dialogRef.current?.querySelectorAll<HTMLElement>(
                    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                ) || []
            );
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
            previouslyFocused?.focus();
        };
    }, [modalVisible]);

    const categoryQuery = useQuery({
        queryKey: ['newOpportunityCategories', centerId],
        queryFn: () => getNewOpportunityCategoriesAPI(centerId, authorization),
        enabled: !!centerId && !!authorization,
    });
    const categories: CategoryOption[] = useMemo(
        () => categoryQuery.data?.data?.results || [],
        [categoryQuery.data?.data?.results]
    );

    const coachQuery = useQuery({
        queryKey: ['newOpportunityCoaches', centerId],
        queryFn: () => getCoachNameListAPI(centerId, authorization),
        enabled: !!centerId && !!authorization,
    });
    const coaches: CoachOption[] = useMemo(
        () => coachQuery.data?.data?.coachNameList || [],
        [coachQuery.data?.data?.coachNameList]
    );

    const selectedCategory = useMemo(
        () => categories.find((category) => getId(category) === form.categoryId),
        [categories, form.categoryId]
    );

    const journalQuery = useQuery({
        queryKey: ['newOpportunityJournal', centerId, newOpportunityId],
        queryFn: () => getNewOpportunityJournalAPI(centerId, newOpportunityId, authorization),
        enabled: !!centerId && !!newOpportunityId && !!authorization,
        retry: false,
    });
    const journalResponse = journalQuery.data?.data;
    const opportunity = journalResponse?.opportunity || detailObject;
    const appLinkState = resolveScheduleAppLink(opportunity);
    const isReadOnly =
        isNewOpportunityReadOnly(opportunity) ||
        isNewOpportunityReadOnly(detailObject) ||
        appLinkState === 'withdrawn';
    const journal = journalResponse?.journal || null;
    const linkedMemberId = opportunity?.memberId || detailObject?.memberId || '';
    const linkedMemberName =
        opportunity?.memberName ||
        detailObject?.memberName ||
        detailObject?.alias ||
        '회원 이름 없음';
    const linkedMemberPhoneNumber =
        formatNewOpportunityPhoneNumber(
            opportunity?.memberPhoneNumber || detailObject?.memberPhoneNumber
        ) || '';
    const linkedMemberType =
        opportunity?.memberType || detailObject?.memberType || '';
    const linkedMemberTypeLabel =
        linkedMemberType === 'public'
            ? '퍼블릭 회원'
            : linkedMemberType === 'temp'
            ? '담당강사 연결 회원'
            : '센터 회원';
    const displayName =
        opportunity?.displayName ||
        selectedCategory?.name ||
        opportunity?.categoryName ||
        opportunity?.name ||
        '수업';

    useEffect(() => {
        setForm(makeNewOpportunityFormState(detailObject));
        setDirtyFields(new Set());
        setConflictNotice('');
    }, [newOpportunityId]);

    useEffect(() => {
        if (!journalResponse?.opportunity) return;
        setForm((current) =>
            mergeLatestNewOpportunityForm(
                current,
                journalResponse.opportunity,
                dirtyFields
            )
        );
    }, [journalResponse?.opportunity, dirtyFields]);

    const updateMutation = useMutation({
        mutationFn: (body: Record<string, any>) =>
            updateNewOpportunityAPI(centerId, newOpportunityId, authorization, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['newOpportunityCalendar'] });
            queryClient.invalidateQueries({ queryKey: ['newOpportunities'] });
            queryClient.invalidateQueries({ queryKey: ['newOpportunitySummary'] });
            // 회원 프로필의 신규기회 히스토리도 갱신
            queryClient.invalidateQueries({ queryKey: ['memberProfileNewOpportunities'] });
            notify('', '신규기회가 저장되었습니다.');
            close();
        },
        onError: async (error: any) => {
            const status = error?.response?.status;
            if (status === 409) {
                const conflict = await recoverNewOpportunityUpdateConflict(
                    error,
                    () => journalQuery.refetch()
                );
                setConflictNotice(conflict.message);
                notify(
                    '변경 내용 충돌',
                    conflict.message,
                    true
                );
                return;
            }
            if (status === 410 || status === 420) {
                notify('저장 불가', '이미 예약된 시간입니다.', true);
                return;
            }
            notify('저장 실패', '신규기회 저장 중 오류가 발생했습니다.', true);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: () => deleteNewOpportunityAPI(centerId, newOpportunityId, authorization),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['newOpportunityCalendar'] });
            queryClient.invalidateQueries({ queryKey: ['newOpportunities'] });
            queryClient.invalidateQueries({ queryKey: ['newOpportunitySummary'] });
            notify('', '신규기회가 삭제되었습니다.');
            close();
        },
        onError: () => notify('삭제 실패', '신규기회 삭제 중 오류가 발생했습니다.', true),
    });

    const openMemberProfile = () => {
        if (!linkedMemberId) return;
        close();
        navigate(`/member/${linkedMemberId}`);
    };

    const openNewOpportunityReport = () => {
        close();
        navigate('/centerReport/newOpportunities');
    };

    const setField = <FieldName extends FormField>(
        field: FieldName,
        value: FormState[FieldName]
    ) => {
        if (isReadOnly) return;
        setForm((prev) => ({ ...prev, [field]: value }));
        setDirtyFields((previous) => {
            const next = new Set(previous);
            next.add(field);
            return next;
        });
    };

    const onSave = () => {
        if (isReadOnly) {
            return notify('', '탈퇴한 회원의 기록은 조회만 가능합니다.', true);
        }
        if (dirtyFields.size === 0) {
            return notify('', '변경된 내용이 없습니다.');
        }
        if (!form.coachId) return notify('필수 입력', '담당 강사를 선택해주세요.', true);
        if (!form.categoryId) return notify('필수 입력', '신규기회 유형을 선택해주세요.', true);
        if (!form.scheduleDate || !form.startClock || !form.endClock) {
            return notify('필수 입력', '날짜와 시간을 입력해주세요.', true);
        }

        const nextStartTime = combineDateAndTime(form.scheduleDate, form.startClock);
        const nextEndTime = combineDateAndTime(form.scheduleDate, form.endClock);

        if (!nextStartTime.isValid() || !nextEndTime.isValid()) {
            return notify('입력 오류', '날짜와 시간을 다시 확인해주세요.', true);
        }
        if (nextEndTime.isSame(nextStartTime) || nextEndTime.isBefore(nextStartTime)) {
            return notify('입력 오류', '종료 시간은 시작 시간보다 늦어야 합니다.', true);
        }

        updateMutation.mutate(
            buildNewOpportunityUpdatePatch(
                form,
                dirtyFields,
                opportunity?.updatedAt || detailObject?.updatedAt
            )
        );
    };

    const onDelete = () => {
        if (isReadOnly) {
            return notify('', '탈퇴한 회원의 기록은 조회만 가능합니다.', true);
        }
        const journalNotice = journal
            ? ' 연결된 운동일지는 삭제되지 않으며 강사·회원 피드에 보존됩니다.'
            : '';
        if (
            !window.confirm(
                `신규기회를 삭제할까요? 삭제 후에는 캘린더와 분석에서 제외됩니다.${journalNotice}`
            )
        ) {
            return;
        }
        deleteMutation.mutate();
    };

    return (
        <ModalOverLay onClick={close}>
            <ModalInner
                ref={dialogRef}
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="new-opportunity-detail-title"
            >
                <Row style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Col>
                        <Row style={{ alignItems: 'center', gap: 12 }}>
                            <Subtitle1Bold
                                id="new-opportunity-detail-title"
                                style={{ color: colors.LAVEL_4, fontSize: 28 }}
                            >
                                신규기회 상세
                            </Subtitle1Bold>
                            <Row style={styles.categoryBadge}>
                                <CaptionBold style={{ color: '#B45309' }}>
                                    {displayName}
                                </CaptionBold>
                            </Row>
                        </Row>
                        <Body3Regular style={{ color: colors.LAVEL_1, marginTop: 10, fontSize: 15 }}>
                            {isReadOnly
                                ? '탈퇴한 회원의 과거 기록을 조회합니다.'
                                : '일정, 담당 강사, 유형, 공유 메모를 수정합니다.'}
                        </Body3Regular>
                    </Col>
                    <CloseButton
                        ref={closeButtonRef}
                        type="button"
                        onClick={close}
                        aria-label="신규기회 상세 닫기"
                    >
                        <img src={I_POPUP_EXIT} width={24} height={24} alt="" />
                    </CloseButton>
                </Row>

                <div style={styles.memberSummaryCard}>
                    <Row style={{ alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                        <Col style={{ minWidth: 0 }}>
                            <div style={styles.summaryLabel}>연결 회원</div>
                            <Row style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                                <Body3Bold style={{ color: '#1A305D', fontSize: 22 }}>{linkedMemberName}</Body3Bold>
                                <span
                                    style={
                                        linkedMemberType === 'public'
                                            ? styles.memberTypeBadge
                                            : styles.memberBadge
                                    }
                                >
                                    {linkedMemberTypeLabel}
                                </span>
                            </Row>
                            <Body3Regular style={{ color: colors.LAVEL_2, marginTop: 8, fontSize: 16 }}>
                                {linkedMemberPhoneNumber || '연락처 없음'}
                            </Body3Regular>
                            <Body3Regular style={styles.summaryHint}>
                                회원 정보가 잘못 연결된 경우 삭제 후 다시 등록해주세요.
                            </Body3Regular>
                            <Row style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                                <span
                                    style={
                                        appLinkState === 'linked'
                                            ? styles.appLinkedBadge
                                            : appLinkState === 'withdrawn'
                                            ? styles.appWithdrawnBadge
                                            : appLinkState === 'unlinked'
                                            ? styles.appNotLinkedBadge
                                            : styles.unknownBadge
                                    }
                                >
                                    {appLinkState === 'linked'
                                        ? '앱 연결 완료'
                                        : appLinkState === 'withdrawn'
                                        ? '앱 탈퇴'
                                        : appLinkState === 'unlinked'
                                        ? '앱 미연결'
                                        : '앱 연결 확인 필요'}
                                </span>
                                <span
                                    style={
                                        journalQuery.isError
                                            ? styles.journalErrorBadge
                                            : journal
                                            ? styles.journalWrittenBadge
                                            : styles.journalEmptyBadge
                                    }
                                >
                                    {journalQuery.isLoading
                                        ? '운동일지 조회 중'
                                        : journalQuery.isError
                                        ? '운동일지 조회 실패'
                                        : journal
                                        ? '운동일지 작성됨'
                                        : '운동일지 미작성'}
                                </span>
                            </Row>
                        </Col>
                        {linkedMemberId && (
                            <button type="button" style={styles.smallSecondaryButton} onClick={openMemberProfile}>
                                회원 프로필
                            </button>
                        )}
                    </Row>
                </div>

                {isReadOnly && (
                    <div role="status" style={styles.readOnlyNotice}>
                        탈퇴한 회원의 데이터는 보존 목적으로 조회만 가능하며 수정·삭제할 수 없습니다.
                    </div>
                )}

                <Body3Bold style={styles.sectionTitle}>운동일지</Body3Bold>
                <div style={{ marginTop: 14 }}>
                    <NewOpportunityJournalReadOnly
                        journal={journal}
                        isLoading={
                            journalQuery.isLoading ||
                            (journalQuery.isFetching && !journalQuery.data)
                        }
                        isError={journalQuery.isError}
                        error={journalQuery.error}
                        onRetry={() => journalQuery.refetch()}
                        displayName={displayName}
                        coachName={opportunity?.coachName || detailObject?.coachName}
                        startTime={opportunity?.startTime || detailObject?.startTime}
                        endTime={opportunity?.endTime || detailObject?.endTime}
                    />
                </div>

                <Body3Bold style={styles.sectionTitle}>
                    {isReadOnly ? '보존 정보' : '수정 정보'}
                </Body3Bold>
                {conflictNotice && (
                    <div role="alert" style={styles.conflictNotice}>
                        {conflictNotice}
                    </div>
                )}
                <div className="new-opportunity-detail-form" style={styles.formGrid}>
                    <Field label="신규기회 유형">
                        <select
                            value={form.categoryId}
                            disabled={isReadOnly}
                            onChange={(e) => setField('categoryId', e.target.value)}
                            style={styles.input}
                        >
                            <option value="">유형 선택</option>
                            {categories.map((category) => (
                                <option key={getId(category)} value={getId(category)}>
                                    {category.name}
                                    {category.isActive === false ? ' (비활성)' : ''}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="담당 강사">
                        <select
                            value={form.coachId}
                            disabled={isReadOnly}
                            onChange={(e) => setField('coachId', e.target.value)}
                            style={styles.input}
                        >
                            <option value="">강사 선택</option>
                            {coaches.map((coach) => (
                                <option key={coach.coachId} value={coach.coachId}>
                                    {coach.name}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="출석">
                        <select
                            value={form.isNoShow ? 'NO_SHOW' : 'AUTO'}
                            disabled={isReadOnly}
                            onChange={(e) => setField('isNoShow', e.target.value === 'NO_SHOW')}
                            style={styles.input}
                        >
                            <option value="AUTO">
                                {combineDateAndTime(
                                    form.scheduleDate,
                                    form.startClock
                                ).isAfter(dayjs())
                                    ? '예정 (자동)'
                                    : '완료 (자동)'}
                            </option>
                            <option value="NO_SHOW">노쇼</option>
                        </select>
                    </Field>
                    <Field label="날짜">
                        <input
                            type="date"
                            value={form.scheduleDate}
                            disabled={isReadOnly}
                            onChange={(e) => setField('scheduleDate', e.target.value)}
                            style={styles.input}
                        />
                    </Field>
                    <div className="new-opportunity-detail-time-range" style={styles.timeRangeField}>
                        <Field label="시작 시간 (10분 단위)">
                            {(() => {
                                const parts = splitClock(form.startClock);
                                const update = (next: typeof parts) =>
                                    setField('startClock', joinClock(next));
                                return (
                                    <div
                                        className="new-opportunity-detail-time-selects"
                                        style={{ display: 'flex', gap: 8 }}
                                    >
                                        <select
                                            value={parts.period}
                                            disabled={isReadOnly}
                                            onChange={(e) => update({ ...parts, period: e.target.value as 'AM' | 'PM' })}
                                            style={styles.timeSelect}
                                        >
                                            <option value="AM">오전</option>
                                            <option value="PM">오후</option>
                                        </select>
                                        <select
                                            value={String(parts.hour12)}
                                            disabled={isReadOnly}
                                            onChange={(e) => update({ ...parts, hour12: Number(e.target.value) })}
                                            style={styles.timeSelect}
                                        >
                                            {HOUR_OPTIONS_12.map((h) => (
                                                <option key={h} value={String(h)}>
                                                    {String(h).padStart(2, '0')}시
                                                </option>
                                            ))}
                                        </select>
                                        <select
                                            value={String(parts.minute)}
                                            disabled={isReadOnly}
                                            onChange={(e) => update({ ...parts, minute: Number(e.target.value) })}
                                            style={styles.timeSelect}
                                        >
                                            {MINUTE_OPTIONS_10.map((m) => (
                                                <option key={m} value={String(m)}>
                                                    {String(m).padStart(2, '0')}분
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            })()}
                        </Field>
                        <Field label="종료 시간 (10분 단위)">
                            {(() => {
                                const parts = splitClock(form.endClock);
                                const update = (next: typeof parts) =>
                                    setField('endClock', joinClock(next));
                                return (
                                    <div
                                        className="new-opportunity-detail-time-selects"
                                        style={{ display: 'flex', gap: 8 }}
                                    >
                                        <select
                                            value={parts.period}
                                            disabled={isReadOnly}
                                            onChange={(e) => update({ ...parts, period: e.target.value as 'AM' | 'PM' })}
                                            style={styles.timeSelect}
                                        >
                                            <option value="AM">오전</option>
                                            <option value="PM">오후</option>
                                        </select>
                                        <select
                                            value={String(parts.hour12)}
                                            disabled={isReadOnly}
                                            onChange={(e) => update({ ...parts, hour12: Number(e.target.value) })}
                                            style={styles.timeSelect}
                                        >
                                            {HOUR_OPTIONS_12.map((h) => (
                                                <option key={h} value={String(h)}>
                                                    {String(h).padStart(2, '0')}시
                                                </option>
                                            ))}
                                        </select>
                                        <select
                                            value={String(parts.minute)}
                                            disabled={isReadOnly}
                                            onChange={(e) => update({ ...parts, minute: Number(e.target.value) })}
                                            style={styles.timeSelect}
                                        >
                                            {MINUTE_OPTIONS_10.map((m) => (
                                                <option key={m} value={String(m)}>
                                                    {String(m).padStart(2, '0')}분
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            })()}
                        </Field>
                    </div>
                </div>

                <Col style={{ marginTop: 26, gap: 10 }}>
                    <Body3Bold style={{ color: colors.LAVEL_4, fontSize: 16 }}>공유 메모</Body3Bold>
                    <textarea
                        value={form.memo}
                        disabled={isReadOnly}
                        onChange={(e) => setField('memo', e.target.value)}
                        style={styles.textarea}
                        placeholder="강사와 센터 관리자가 함께 확인할 메모"
                    />
                </Col>

                <Row className="new-opportunity-detail-actions" style={styles.actionRow}>
                    {!isReadOnly && (
                        <button
                            type="button"
                            style={styles.deleteButton}
                            onClick={onDelete}
                            disabled={deleteMutation.isLoading}
                        >
                            삭제
                        </button>
                    )}
                    <Row style={{ gap: 12 }}>
                        <button type="button" style={styles.secondaryButton} onClick={openNewOpportunityReport}>
                            분석 화면 보기
                        </button>
                        {!isReadOnly && (
                            <button
                                type="button"
                                style={{
                                    ...styles.primaryButton,
                                    opacity:
                                        updateMutation.isLoading ||
                                        dirtyFields.size === 0
                                            ? 0.55
                                            : 1,
                                }}
                                onClick={onSave}
                                disabled={
                                    updateMutation.isLoading ||
                                    dirtyFields.size === 0
                                }
                            >
                                {updateMutation.isLoading ? '저장 중' : '저장'}
                            </button>
                        )}
                    </Row>
                </Row>
            </ModalInner>
        </ModalOverLay>
    );
};

const styles: Record<string, React.CSSProperties> = {
    categoryBadge: {
        height: 32,
        maxWidth: 280,
        borderRadius: 999,
        border: '1px solid #FCD34D',
        backgroundColor: '#FFFBEB',
        alignItems: 'center',
        padding: '0 12px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    memberSummaryCard: {
        marginTop: 26,
        border: '1px solid #C7D2FE',
        background: '#EEF4FF',
        borderRadius: 14,
        padding: '20px 22px',
        minWidth: 0,
    },
    summaryLabel: {
        color: '#64748B',
        fontSize: 13,
        fontWeight: 850,
    },
    summaryHint: {
        color: '#64748B',
        fontSize: 13,
        lineHeight: '19px',
        marginTop: 12,
    },
    sectionTitle: {
        color: colors.LAVEL_4,
        fontSize: 18,
        marginTop: 30,
    },
    conflictNotice: {
        marginTop: 14,
        border: '1px solid #FCA5A5',
        borderRadius: 10,
        backgroundColor: '#FEF2F2',
        color: '#991B1B',
        padding: '12px 14px',
        fontSize: 13,
        lineHeight: '20px',
        fontWeight: 750,
    },
    readOnlyNotice: {
        marginTop: 18,
        border: '1px solid #FCD34D',
        borderRadius: 10,
        backgroundColor: '#FFFBEB',
        color: '#92400E',
        padding: '12px 14px',
        fontSize: 14,
        lineHeight: '20px',
        fontWeight: 750,
    },
    formGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '22px 18px',
        marginTop: 16,
    },
    timeRangeField: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 18,
        minWidth: 0,
        gridColumn: '1 / -1',
    },
    statusHistoryPanel: {
        marginTop: 14,
        border: '1px solid #E2E8F0',
        borderRadius: 12,
        background: '#F8FAFC',
        padding: '4px 16px',
    },
    statusHistoryRow: {
        display: 'grid',
        gridTemplateColumns: '10px minmax(0, 1fr)',
        gap: 12,
        alignItems: 'flex-start',
        padding: '13px 0',
        borderBottom: '1px solid #E2E8F0',
    },
    statusHistoryDot: {
        width: 8,
        height: 8,
        borderRadius: 999,
        marginTop: 6,
        background: '#3D73DD',
        boxShadow: '0 0 0 3px #DBEAFE',
    },
    statusHistoryTransition: {
        color: '#1A305D',
        fontSize: 14,
        lineHeight: '20px',
        fontWeight: 850,
    },
    statusHistoryMeta: {
        color: '#64748B',
        fontSize: 12,
        lineHeight: '18px',
        fontWeight: 700,
        marginTop: 3,
    },
    statusHistoryEmpty: {
        color: '#64748B',
        fontSize: 13,
        lineHeight: '20px',
        padding: '12px 0',
    },
    memberBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 9px',
        borderRadius: 999,
        background: '#1D4ED8',
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: 850,
    },
    memberTypeBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 9px',
        borderRadius: 999,
        border: '1px solid #BFDBFE',
        background: '#FFFFFF',
        color: '#1D4ED8',
        fontSize: 12,
        fontWeight: 800,
    },
    appLinkedBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 25,
        padding: '0 9px',
        borderRadius: 999,
        background: '#ECFDF5',
        border: '1px solid #A7F3D0',
        color: '#047857',
        fontSize: 12,
        fontWeight: 850,
    },
    appNotLinkedBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 25,
        padding: '0 9px',
        borderRadius: 999,
        background: '#FFF7ED',
        border: '1px solid #FED7AA',
        color: '#C2410C',
        fontSize: 12,
        fontWeight: 850,
    },
    appWithdrawnBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 25,
        padding: '0 9px',
        borderRadius: 999,
        background: '#FFE8E9',
        border: '1px solid #FFC1C5',
        color: '#B42318',
        fontSize: 12,
        fontWeight: 850,
    },
    unknownBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 25,
        padding: '0 9px',
        borderRadius: 999,
        background: '#F8FAFC',
        border: '1px solid #CBD5E1',
        color: '#64748B',
        fontSize: 12,
        fontWeight: 850,
    },
    journalWrittenBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 25,
        padding: '0 9px',
        borderRadius: 999,
        background: '#F5F3FF',
        border: '1px solid #DDD6FE',
        color: '#6D28D9',
        fontSize: 12,
        fontWeight: 850,
    },
    journalEmptyBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 25,
        padding: '0 9px',
        borderRadius: 999,
        background: '#F8FAFC',
        border: '1px solid #E2E8F0',
        color: '#64748B',
        fontSize: 12,
        fontWeight: 850,
    },
    journalErrorBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 25,
        padding: '0 9px',
        borderRadius: 999,
        background: '#FEF2F2',
        border: '1px solid #FECACA',
        color: '#B91C1C',
        fontSize: 12,
        fontWeight: 850,
    },
    smallSecondaryButton: {
        height: 40,
        border: '1px solid #BFD0F5',
        borderRadius: 10,
        background: '#FFFFFF',
        color: '#1A305D',
        padding: '0 16px',
        fontSize: 14,
        fontWeight: 850,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    input: {
        height: 48,
        borderRadius: 10,
        border: '1px solid #D8DEE9',
        backgroundColor: '#FFFFFF',
        padding: '0 14px',
        color: '#1A305D',
        fontSize: 15,
        outline: 'none',
        boxSizing: 'border-box',
        width: '100%',
    },
    timeSelect: {
        height: 48,
        borderRadius: 10,
        border: '1px solid #D8DEE9',
        backgroundColor: '#FFFFFF',
        padding: '0 10px',
        color: '#1A305D',
        fontSize: 15,
        outline: 'none',
        boxSizing: 'border-box',
        flex: 1,
        minWidth: 0,
    },
    textarea: {
        minHeight: 118,
        borderRadius: 10,
        border: '1px solid #D8DEE9',
        backgroundColor: '#FFFFFF',
        padding: 14,
        color: '#1A305D',
        fontSize: 15,
        lineHeight: '23px',
        outline: 'none',
        resize: 'vertical',
        boxSizing: 'border-box',
    },
    actionRow: {
        marginTop: 32,
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
    },
    deleteButton: {
        height: 48,
        minWidth: 112,
        borderRadius: 10,
        border: '1px solid #FCA5A5',
        background: '#FEF2F2',
        color: '#DC2626',
        fontSize: 15,
        fontWeight: 850,
        cursor: 'pointer',
    },
    secondaryButton: {
        height: 48,
        minWidth: 148,
        borderRadius: 10,
        border: '1px solid #A6BFEF',
        background: '#FFFFFF',
        color: colors.BLUE_500,
        fontSize: 15,
        fontWeight: 850,
        cursor: 'pointer',
    },
    primaryButton: {
        height: 48,
        minWidth: 148,
        borderRadius: 10,
        border: '1px solid #3D73DD',
        background: '#3D73DD',
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: 850,
        cursor: 'pointer',
    },
};

export default NewOpportunityDetailModal;
